/**
 * Role-based permission helpers.
 *
 * Pure functions used by BOTH:
 *   - API route guards (`if (!canDeleteItem(user, item)) return 403`)
 *   - UI conditional rendering (`{canManageClients(user.role) && <Button />}`)
 *
 * Adding a new role/permission?
 *   1. Add it to `reference/roles.ts` (rank + enum value)
 *   2. Add a check here (pure function)
 *   3. Wire it into the API guard AND the UI hide/show
 *   4. Add a smoke test
 *
 * **DO NOT** replicate permission checks in components or routes. Import from here.
 */

import type { AuthUser } from '../types/api';
import { roleAtLeast, STAFF_ROLES } from '../reference/roles';

export function isClient(user: AuthUser): boolean {
  return user.role === 'CLIENT';
}

export function isStaff(user: AuthUser): boolean {
  return (STAFF_ROLES as readonly string[]).includes(user.role);
}

/** CEO/GM only — manage users, settings, destructive bulk ops. */
export function canManageUsers(user: AuthUser): boolean {
  return user.role === 'CEO' || user.role === 'GM';
}

/** CEO/GM/Manager — author/own engagements. */
export function canAuthorEngagement(user: AuthUser): boolean {
  return roleAtLeast(user.role as never, 'Manager');
}

/** CEO/GM only — bulk delete on clients/items. */
export function canBulkDelete(user: AuthUser): boolean {
  return user.role === 'CEO' || user.role === 'GM';
}

/** CEO/GM/Manager — create/edit clients. */
export function canManageClients(user: AuthUser): boolean {
  return roleAtLeast(user.role as never, 'Manager');
}

/** CEO/GM/Manager/TeamLead — create/edit compliance items. */
export function canEditCompliance(user: AuthUser): boolean {
  return roleAtLeast(user.role as never, 'TeamLead');
}

/** Anyone staff-side — log time entries. */
export function canLogTime(user: AuthUser): boolean {
  return isStaff(user);
}

/** Mark item Complete — staff only (per workflow spec). */
export function canCompleteItem(user: AuthUser): boolean {
  return isStaff(user);
}

/** Mark item Reviewed / Sent Back / Sent to Client / Completed — TeamLead+. */
export function canReviewItem(user: AuthUser): boolean {
  return roleAtLeast(user.role as never, 'TeamLead');
}

/** Manage billing / WIP / invoice ops — CEO/GM. */
export function canManageBilling(user: AuthUser): boolean {
  return user.role === 'CEO' || user.role === 'GM';
}

/** Clients can only see their own engagement + items. Resource-level scoping is
 *  enforced server-side; UI hides controls they can't use. */
export function canViewClientPortal(user: AuthUser): boolean {
  return isClient(user) || isStaff(user);
}

/** CEO only — delete audit logs (extreme case, off by default). */
export function canPurgeAuditLog(user: AuthUser): boolean {
  return user.role === 'CEO';
}